import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/components/ui/use-toast';
import { useTheme } from '@/components/theme-provider';
import { 
  Moon, 
  Sun, 
  Mail, 
  Lock, 
  Eye, 
  EyeOff, 
  ShieldCheck,
  ArrowRight
} from 'lucide-react';

export default function LoginPage() {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const { toast } = useToast();
  const { theme, setTheme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const cleanEmail = formData.email.trim().toLowerCase();

    try {
      await signIn(cleanEmail, formData.password);
      toast({
        title: 'Login realizado com sucesso!',
        description: 'Bem-vindo ao Sistema Metalúrgica.',
      });
      navigate('/');
    } catch (err) {
      setError('Email ou senha inválidos. Por favor, confira os dados informados.');
      toast({
        variant: 'destructive',
        title: 'Falha na autenticação',
        description: 'Verifique seu e-mail e senha e tente novamente.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col justify-center items-center p-4 sm:p-6 relative bg-gradient-to-b from-zinc-50 to-zinc-100/80 dark:from-zinc-950 dark:to-zinc-900 transition-colors duration-300">
      {/* Botão de Alternância de Tema */}
      <div className="absolute top-5 right-5 z-20">
        <Button
          variant="ghost"
          size="icon"
          className="rounded-full w-9 h-9 text-muted-foreground hover:text-foreground hover:bg-background/80 transition-colors"
          onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
          title={theme === 'light' ? 'Modo escuro' : 'Modo claro'}
        >
          {theme === 'light' ? (
            <Moon className="h-4 w-4" />
          ) : (
            <Sun className="h-4 w-4 text-amber-400" />
          )}
          <span className="sr-only">Alternar tema</span>
        </Button>
      </div>

      {/* Luz Sutil Decorativa de Fundo */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-emerald-500/10 dark:bg-emerald-500/5 rounded-full blur-3xl pointer-events-none" />

      {/* Conteúdo Central */}
      <div className="w-full max-w-[420px] relative z-10 space-y-6">
        {/* Cabeçalho com a Logo Oficial */}
        <div className="text-center space-y-3">
          <div className="flex justify-center">
            <img
              src="/logo.png"
              alt="Grupo Plantão"
              className="h-20 w-20 object-contain drop-shadow-sm hover:scale-105 transition-transform duration-300"
            />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              Sistema Metalúrgica
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">
              Grupo Plantão • Gestão Industrial Integrada
            </p>
          </div>
        </div>

        {/* Card do Formulário */}
        <div className="bg-card/90 dark:bg-zinc-900/90 backdrop-blur-md border border-border/80 shadow-xl shadow-zinc-200/50 dark:shadow-none rounded-2xl p-6 sm:p-8 transition-all">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive" className="border-destructive/30 bg-destructive/10 text-destructive text-xs py-2.5">
                <AlertDescription className="font-medium">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            {/* Campo E-mail */}
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-medium text-foreground">
                E-mail
              </Label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-muted-foreground">
                  <Mail className="h-4 w-4" />
                </div>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu.email@grupoplantao.com.br"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 h-10 bg-background/80 border-border focus-visible:ring-emerald-500/30 focus-visible:border-emerald-600 text-sm transition-all"
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Campo Senha */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs font-medium text-foreground">
                  Senha
                </Label>
                <button
                  type="button"
                  onClick={() => navigate('/auth/reset-password')}
                  className="text-xs text-muted-foreground hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                >
                  Esqueceu a senha?
                </button>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-muted-foreground">
                  <Lock className="h-4 w-4" />
                </div>
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 pr-10 h-10 bg-background/80 border-border focus-visible:ring-emerald-500/30 focus-visible:border-emerald-600 text-sm transition-all"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-muted-foreground hover:text-foreground transition-colors"
                  title={showPassword ? 'Ocultar senha' : 'Exibir senha'}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Botão de Login */}
            <Button
              type="submit"
              className="w-full h-10 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-medium text-sm rounded-lg shadow-sm transition-all mt-2 flex items-center justify-center gap-2"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white" />
                  <span>Entrando...</span>
                </div>
              ) : (
                <>
                  <span>Entrar</span>
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
          </form>
        </div>

        {/* Rodapé Minimalista */}
        <div className="text-center flex items-center justify-center gap-1.5 text-xs text-muted-foreground/70">
          <ShieldCheck className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
          <span>Acesso restrito e monitorado • Supabase Auth</span>
        </div>
      </div>
    </div>
  );
}